import {
  isCancelledError,
  queryOptions,
  type FetchQueryOptions,
  type QueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import type { ProjectService } from "@/services/project.service";
import type { TPartialProject, TProject, TWorkspaceProjectRow } from "@/types/project";
import type { IPriorityBreakdown } from "@/types/request-prioritization";
import { getQueryClient, peekQueryClient } from "./client";
import { dropOnDenial } from "./invalidation";
import { FRESHNESS, withinAttempt } from "./policies";
import {
  invalidateProjectsTable,
  patchProjectsTableRows,
  removeProjectsTableRows,
} from "./projects-table";
import {
  scopedKey,
  scopedRead,
  scopeFor,
  scopeIfReady,
  ScopeNotReadyError,
  type QueryScope,
} from "./scope";

/**
 * The project reads the caching pilot moves onto Query (NG-2543, contract §1):
 *
 * - **R1** `project.detail`: one project, `[slug, uid, 'project', id]`. Mutable,
 *   because it is working data a screen edits and another tab may change.
 * - **R2** `project.bank`: every project in the workspace, walked across all
 *   pages, `[slug, uid, 'projects', 'bank', {archived}]`. Reference data: the
 *   pickers and filters that read it need names and ids, which rarely change.
 *
 * R1 sits under `'project'` so a prefix invalidation of `('project', id)` also
 * reaches the sub-resources (R3 onwards) keyed beneath it; R2 sits under
 * `'projects'` so it does not. Both are plain idempotent GETs, so both declare
 * `"safe-read"` and `runRead` owns their retries.
 *
 * The definitions take the service rather than reaching for the root store, so
 * a loader passes `getRootStore().projectService` and a test passes a fake.
 *
 * These two are the only copy of a project's detail and of the workspace's
 * project list (NG-2794): the project store keeps the Projects table's pages
 * and filters (NG-2804), not either of these. The helpers below are how a
 * reader outside a component and every project writer reach them.
 */

/** The R1 key. */
export function projectDetailKey(scope: QueryScope, projectId: string) {
  return scopedKey(scope, "project", projectId);
}

/**
 * The R2 key. `archived` is part of the representation: the backend lists
 * active projects unless asked, and the two lists are separate entries.
 */
export function projectBankKey(
  scope: QueryScope,
  { archived = false }: { archived?: boolean } = {},
) {
  return scopedKey(scope, "projects", "bank", { archived });
}

/**
 * Who the project connects and its Leads who lost their connection (NG-2967,
 * NG-3080), `[slug, uid, 'project', id, 'lead-connection']`: under the project's key, so a
 * project write (its teams, its Leads) re-reads it with the detail.
 */
export function projectLeadConnectionQuery(
  service: Pick<ProjectService, "getLeadConnection">,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: scopedKey(scope, "project", projectId, "lead-connection"),
    queryFn: scopedRead(() => service.getLeadConnection(scope.tenant, projectId), {
      retry: "safe-read",
    }),
  });
}

/**
 * Which of `userIds` an assignment leaves outside the project (NG-2976, spec 12.6),
 * `[slug, uid, 'project', id, 'outside-assignees', {users}]`, the ids sorted so the
 * same assignees share one entry. A permission answer, so access-sensitive; under
 * the project's key, so a project write (its teams, its members) re-reads it.
 */
export function projectOutsideAssigneesQuery(
  service: Pick<ProjectService, "getOutsideAssignees">,
  scope: QueryScope,
  projectId: string,
  userIds: readonly string[],
) {
  const users = [...userIds].sort();
  return queryOptions({
    ...FRESHNESS["access-sensitive"],
    queryKey: scopedKey(scope, "project", projectId, "outside-assignees", { users }),
    queryFn: scopedRead(() => service.getOutsideAssignees(scope.tenant, projectId, users), {
      retry: "safe-read",
    }),
  });
}

/**
 * The project's own priority score, line by line (NG-3071),
 * `[slug, uid, 'project', id, 'priority-breakdown']`: under the project's key,
 * so a project write (its programs, goals or custom fields) re-reads it with the
 * detail. `null` while scoring is off. Read only by the Plan Work timeline's
 * breakdown window.
 *
 * Reference freshness, not mutable: the bar beside it shows the score from the
 * timeline's one list read, which never refreshes while open, so re-reading the
 * breakdown every minute would only let the two disagree.
 *
 * @writers A project write that invalidates `('project', id)` re-reads it. A
 * published change to the scoring rules invalidates nothing here, so it shows
 * once the 5 min `reference` reuse runs out.
 */
export function projectPriorityBreakdownQuery(
  service: Pick<ProjectService, "getPriorityBreakdown">,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: scopedKey(scope, "project", projectId, "priority-breakdown"),
    queryFn: scopedRead<IPriorityBreakdown | null>(
      async (request) =>
        (await service.getPriorityBreakdown(scope.tenant, projectId, request)).breakdown,
      { retry: "safe-read" },
    ),
  });
}

/** R1: one project's detail, in `scope`'s workspace. */
export function projectDetailQuery(
  service: Pick<ProjectService, "getProject">,
  scope: QueryScope,
  projectId: string,
) {
  return queryOptions({
    ...FRESHNESS.mutable,
    queryKey: projectDetailKey(scope, projectId),
    queryFn: scopedRead((request) => service.getProject(scope.tenant, projectId, request), {
      retry: "safe-read",
    }),
  });
}

/**
 * R2: the workspace's whole project list, every page. `archived: false` sends
 * no parameter, so it lists the active projects.
 *
 * A retry restarts the walk from the first page. `runRead` sizes one timeout
 * per attempt, and the walk makes a request per page, so the whole walk is
 * bounded by it rather than each page: past it the walk aborts, which `runRead`
 * treats as final, and the skeleton never outlives the interactive deadline.
 */
export function projectBankQuery(
  service: Pick<ProjectService, "fetchAll">,
  scope: QueryScope,
  { archived = false }: { archived?: boolean } = {},
) {
  return queryOptions({
    ...FRESHNESS.reference,
    queryKey: projectBankKey(scope, { archived }),
    queryFn: scopedRead(
      (request) =>
        withinAttempt(request, (bounded) =>
          service.fetchAll(scope.tenant, archived ? { archived: true } : {}, bounded),
        ),
      { retry: "safe-read" },
    ),
  });
}

/**
 * How many times one read starts again after writes cancel it. A burst of
 * writes (two projects created a second apart, each with its own hint) cancels
 * the restart too, so one restart is not enough (NG-3311); a bound still ends
 * a read that writes never stop cancelling.
 */
const MAX_WRITE_RESTARTS = 5;

/**
 * Read `query` through the cache. A write landing while it is in flight
 * cancels it: with something cached the read then answers what the write left
 * there, and with nothing cached it rejects as cancelled, so it is read again,
 * after the write, and again after each write that cancels that read, up to
 * `MAX_WRITE_RESTARTS` times. A sign-out or account switch cancels it too, and
 * then it is not read again: `scope` is the previous user's. A failure is the
 * query's own (`runRead` retries).
 */
async function readPastWrite<T>(
  client: QueryClient,
  scope: QueryScope,
  query: FetchQueryOptions<T, Error, T, QueryKey>,
): Promise<T> {
  for (let restart = 0; ; restart += 1) {
    try {
      return await client.fetchQuery(query);
    } catch (error) {
      if (!isCancelledError(error) || !stillSignedInAs(scope)) throw error;
      if (restart >= MAX_WRITE_RESTARTS) {
        console.warn("[projects] a read was cancelled by writes on every restart", {
          queryKey: query.queryKey,
          restarts: restart,
        });
        throw error;
      }
    }
  }
}

/** Whether `scope`'s user is still the one signed in. */
function stillSignedInAs(scope: QueryScope): boolean {
  try {
    return scopeFor(scope.tenant).principal === scope.principal;
  } catch (error) {
    if (error instanceof ScopeNotReadyError) return false;
    throw error;
  }
}

/** `[slug, uid, 'projects', 'bank']`: both `archived` lists. */
function projectBanksKey(scope: QueryScope) {
  return scopedKey(scope, "projects", "bank");
}

/**
 * One project's detail for a reader outside a component (a route loader, the
 * project wrapper's check): from the cache when fresh, else read and cached.
 * Before the session resolves it reads straight from the service, uncached
 * (the cold-load check).
 *
 * A write that lands while the read is in flight cancels it and puts its own
 * answer in the cache ({@link patchProject}), or drops the entry: the read then
 * asks again, after the write, rather than hand back a detail from before it.
 *
 * A 403 or 404 means this user cannot read the project: everything cached for
 * it goes, its list rows included, and the error is thrown for the caller to
 * show.
 */
export async function readProjectDetail(
  service: Pick<ProjectService, "getProject">,
  slug: string,
  projectId: string,
): Promise<TProject> {
  const scope = scopeIfReady(slug, "project detail");
  if (!scope) return service.getProject(slug, projectId);
  const client = getQueryClient();
  try {
    return await readPastWrite(client, scope, projectDetailQuery(service, scope, projectId));
  } catch (error) {
    if (dropOnDenial(client, scope, error, "project", projectId)) {
      removeBankRow(client, scope, projectId);
    }
    throw error;
  }
}

/**
 * Read the project's detail again now, past any freshness: after a write
 * elsewhere changed what it shows (its status's side effects, its members).
 *
 * A read already in flight is cancelled rather than joined: it started before
 * the write, and its answer would be how "my change did not apply" looks
 * (NG-980).
 */
export async function refreshProjectDetail(
  service: Pick<ProjectService, "getProject">,
  slug: string,
  projectId: string,
): Promise<TProject> {
  const scope = scopeIfReady(slug, "project detail refresh");
  if (!scope) return service.getProject(slug, projectId);
  const client = getQueryClient();
  const query = projectDetailQuery(service, scope, projectId);
  await client.cancelQueries({ queryKey: query.queryKey, exact: true });
  return client.fetchQuery({ ...query, staleTime: 0 });
}

/**
 * The workspace's project list for a reader outside a component: from the
 * cache when fresh, else walked and cached. Before the session resolves it
 * walks straight from the service, uncached.
 */
export async function readProjectBank(
  service: Pick<ProjectService, "fetchAll">,
  slug: string,
): Promise<TPartialProject[]> {
  const scope = scopeIfReady(slug, "project bank");
  if (!scope) return service.fetchAll(slug, {});
  return readPastWrite(getQueryClient(), scope, projectBankQuery(service, scope));
}

/** The project as this tab last read it: its detail, else its active list row. */
export function cachedProject(
  client: QueryClient,
  scope: QueryScope,
  projectId: string,
): TProject | TPartialProject | undefined {
  const detail = client.getQueryData<TProject>(projectDetailKey(scope, projectId));
  if (detail) return detail;
  return client
    .getQueryData<TPartialProject[]>(projectBankKey(scope))
    ?.find((p) => p.id === projectId);
}

/**
 * The list endpoint annotates each row with its work-item counts; the detail
 * and update responses carry them as 0 when the row is not annotated. A patch
 * from either must never overwrite a list row's counts.
 */
function withoutListCounts<T extends object>(patch: T): T {
  if (!("total_issues" in patch) && !("completed_issues" in patch)) return patch;
  const { total_issues: _t, completed_issues: _c, ...rest } = patch as Record<string, unknown>;
  void _t;
  void _c;
  return rest as T;
}

/**
 * After a write that knows the project's new fields: merge `patch` into its
 * cached detail and its row in each cached list, with no request. A read of
 * either already in flight is cancelled first: it may have been answered
 * before the write committed, and landing after the patch it would undo it.
 *
 * Only a write whose answer is a whole detail (`seed`) creates a detail entry
 * that was not there; a list with nothing cached stays uncached.
 */
export function patchProject(
  client: QueryClient,
  scope: QueryScope,
  projectId: string,
  patch: Partial<TProject>,
  { seed = false }: { seed?: boolean } = {},
): void {
  const key = projectDetailKey(scope, projectId);
  void client.cancelQueries({ queryKey: key, exact: true });
  client.setQueryData<TProject>(key, (cached) =>
    cached ? { ...cached, ...patch } : seed ? (patch as TProject) : undefined,
  );
  const rowPatch = withoutListCounts(patch as Partial<TPartialProject>);
  for (const bank of client.getQueryCache().findAll({ queryKey: projectBanksKey(scope) })) {
    // A walk in flight may have read the row before the write, and nothing
    // here could patch what it returns: restart it, so it reads after.
    if (bank.state.fetchStatus === "fetching") {
      void client.cancelQueries({ queryKey: bank.queryKey, exact: true });
    }
    // Only a list holding the row is written: a write marks the entry fresh.
    const rows = client.getQueryData<TPartialProject[]>(bank.queryKey);
    if (!rows?.some((row) => row.id === projectId)) continue;
    client.setQueryData<TPartialProject[]>(
      bank.queryKey,
      rows.map((row) => (row.id === projectId ? { ...row, ...rowPatch } : row)),
    );
  }
  // The Projects table's rows (NG-2804), every filter's, when it is cached.
  patchProjectsTableRows(client, scope, projectId, rowPatch as Partial<TWorkspaceProjectRow>);
}

/**
 * Put a detail the caller already holds in the cache, as a write's answer or a
 * read made elsewhere: the detail, and its row in the active list.
 */
function primeProject(client: QueryClient, scope: QueryScope, project: TProject): void {
  patchProject(client, scope, project.id, project, { seed: true });
}

/**
 * After a create or a duplicate: cache the new project's detail and list it at
 * once in the active list, if that is cached. The list is then marked stale,
 * so its next reader walks it again, while what is on screen keeps the row.
 */
export function appendProject(client: QueryClient, scope: QueryScope, project: TProject): void {
  primeProject(client, scope, project);
  const key = projectBankKey(scope);
  void client.cancelQueries({ queryKey: key, exact: true });
  client.setQueryData<TPartialProject[]>(key, (rows) =>
    rows && !rows.some((p) => p.id === project.id) ? [...rows, project] : rows,
  );
  void client.invalidateQueries({ queryKey: projectBanksKey(scope), refetchType: "none" });
  // Where the new row sits in each filter and sort is the server's answer.
  void invalidateProjectsTable(client, scope);
}

function removeBankRow(client: QueryClient, scope: QueryScope, projectId: string): void {
  for (const bank of client.getQueryCache().findAll({ queryKey: projectBanksKey(scope) })) {
    // A walk in flight may still list the project: restart it.
    if (bank.state.fetchStatus === "fetching") {
      void client.cancelQueries({ queryKey: bank.queryKey, exact: true });
    }
    const rows = client.getQueryData<TPartialProject[]>(bank.queryKey);
    if (!rows?.some((p) => p.id === projectId)) continue;
    client.setQueryData<TPartialProject[]>(
      bank.queryKey,
      rows.filter((p) => p.id !== projectId),
    );
  }
}

/**
 * After a delete, or a denial: drop the project's detail and everything
 * cached under it, and its row in each list, so no reader shows it again.
 *
 * `keepDetail` keeps the detail entry itself, for a reader observing it that
 * has just been refused: the entry holds the refusal, which the reader shows,
 * and dropping it would only have that reader ask again.
 */
export function forgetProject(
  client: QueryClient,
  scope: QueryScope,
  projectId: string,
  { keepDetail = false }: { keepDetail?: boolean } = {},
): void {
  const key = projectDetailKey(scope, projectId);
  client.removeQueries({
    queryKey: key,
    predicate: ({ queryKey }) => !keepDetail || queryKey.length > key.length,
  });
  removeBankRow(client, scope, projectId);
  removeProjectsTableRows(client, scope, projectId);
}

/**
 * After a write that moved projects between the lists, or changed rows this
 * tab cannot patch (archive, restore, a bulk change): both lists are read
 * again, now if a reader shows them, else on their next read. A walk already
 * in flight is restarted, so it cannot settle a list from before the write.
 */
export function invalidateProjectBanks(client: QueryClient, scope: QueryScope): Promise<void> {
  return client.invalidateQueries({ queryKey: projectBanksKey(scope) });
}

/**
 * After a bulk change the server resolved from filters: every project's
 * cached detail in the workspace is read again on its next use.
 */
export function invalidateProjectDetails(client: QueryClient, scope: QueryScope): Promise<void> {
  return client.invalidateQueries({ queryKey: scopedKey(scope, "project") });
}

/**
 * The signed-in query cache for `slug`, for a writer outside a component to
 * keep in step with its write. `null` with no client, or before the session
 * resolves, when nothing is cached under a principal yet.
 */
export function projectCacheFor(slug: string): { client: QueryClient; scope: QueryScope } | null {
  const client = peekQueryClient();
  if (!client) return null;
  try {
    return { client, scope: scopeFor(slug) };
  } catch (error) {
    if (!(error instanceof ScopeNotReadyError)) throw error;
    return null;
  }
}

/**
 * A write outside the project writers changed what the project's detail shows
 * (its members, and with them `project_leads` and `is_member`): read it again,
 * now if a reader shows it, else on its next read. A read already in flight
 * is restarted, so it cannot settle a detail from before the write.
 */
export function markProjectDetailChanged(slug: string, projectId: string): void {
  const cache = projectCacheFor(slug);
  if (!cache) return;
  const { client } = cache;
  const filters = { queryKey: projectDetailKey(cache.scope, projectId), exact: true };
  // A read in flight may have been answered before the write, and its success
  // would clear the mark: restart it first.
  const restart = client.isFetching(filters) > 0 ? client.cancelQueries(filters) : null;
  void client.invalidateQueries(filters);
  void restart?.then(() => client.invalidateQueries(filters));
}
